import { Firestore, collection, doc, getDoc, getDocs, query, where, Timestamp } from "firebase/firestore";
import { db as defaultDb } from "./firebaseService";
import { copyPhotoFile } from "./photoStorageService";
import { createCampPhoto, addPhotoToLocationVersion } from "./campPhotoService";
import type { LocationPhoto } from "@/types/campPhoto";

export async function copyPhotosFromLocation(
	db: Firestore = defaultDb,
	sourceLocationId: string,
	destinationLocationId: string,
	userId: string,
	locationAccessGroupId?: string | null
): Promise<{ success: boolean; copiedCount: number; errors: string[] }> {
	const errors: string[] = [];
	let copied = 0;

	try {
		const destLocRef = doc(db, "campLocations", destinationLocationId);
		const destLocSnap = await getDoc(destLocRef);
		if (!destLocSnap.exists()) throw new Error(`Destination location ${destinationLocationId} not found`);

		const locData: any = destLocSnap.data();
		const providedByOrg = locData?.providedByOrg;
		const orgId =
			typeof providedByOrg === "string"
				? providedByOrg
				: (typeof providedByOrg === "object" && providedByOrg?.id) ? providedByOrg.id : "";

		if (!orgId) throw new Error("Destination location missing organization");

		const sourceLocRef = doc(db, "campLocations", sourceLocationId);
		const q = query(collection(db, "campPhotos"), where("usedBy", "array-contains", sourceLocRef));
		const snap = await getDocs(q);

		const destLocData = destLocSnap.data() || {};
		const existingPhotos: LocationPhoto[] = destLocData.photos || [];
		const nextOrderBase = existingPhotos.length;

		for (const docSnap of snap.docs) {
			const src = docSnap.data() as any;

			const srcPath: string = src.path;
			if (!srcPath) {
				errors.push(`Photo ${docSnap.id} missing path`);
				continue;
			}

			try {
				// 1) Copy the storage object and ensure it succeeds
				const { path: newPath, url: newUrl } = await copyPhotoFile(srcPath, orgId);

				// 2) Create the campPhotos document using the storage path
				const newCampPhotoData: any = {
					path: newPath,
					photoReference: src.photoReference || "",
					permissionStatus: src.permissionStatus || "granted",
					permissionNote: src.permissionNote || "",
					permissionInternalNote: src.permissionInternalNote || "",
					caption: src.caption || "",
					altText: src.altText || "",
					yOffset: typeof src.yOffset === "number" ? src.yOffset : 0,
					organizationId: orgId,
					createdBy: doc(db, "users", userId)
				};
				if (locationAccessGroupId) {
					newCampPhotoData.locationAccessGroupId = locationAccessGroupId;
				} else if (typeof src.locationAccessGroupId === "string") {
					newCampPhotoData.locationAccessGroupId = src.locationAccessGroupId;
				}

				const { photoDoc } = await createCampPhoto(newCampPhotoData, destinationLocationId, userId);

				// 3) Add to destination location version using the public URL for display
				const locationPhoto: LocationPhoto = {
					path: newUrl, // Use download URL for UI rendering
					photoReference: newCampPhotoData.photoReference,
					caption: newCampPhotoData.caption,
					altText: newCampPhotoData.altText,
					yOffset: newCampPhotoData.yOffset,
					campPhotoId: photoDoc,
					createdAt: Timestamp.now(),
					createdBy: doc(db, "users", userId),
					order: nextOrderBase + copied,
					permissionStatus: newCampPhotoData.permissionStatus,
					permissionNote: newCampPhotoData.permissionNote,
					permissionInternalNote: newCampPhotoData.permissionInternalNote
				};

				await addPhotoToLocationVersion(destinationLocationId, userId, locationPhoto);
				copied += 1;
			} catch (e: any) {
				console.error("[copyPhotosService] Failed to copy photo", docSnap.id, e);
				errors.push(`Photo ${docSnap.id}: ${e?.message || "unknown error"}`);
			}
		}

		return { success: errors.length === 0, copiedCount: copied, errors };
	} catch (e: any) {
		errors.push(e?.message || "copy failed");
		return { success: false, copiedCount: copied, errors };
	}
}
